// built-from: /knowledge-retrieval v1 impl:knowledge-retrieval role:implementation
// useKnowledgePages - the API boundary for a page the consultant files or
// removes (093 checks 4 and 5). The companion's Save as page action and the
// Files view's Remove page action reach the knowledge routes only through here
// (constitution III: never `fetch` inside a component).
//
// Every call resolves a typed result and never throws: a refusal or a transport
// failure degrades to `{ ok: false }` so the caller can say what failed and
// what to do next. Nothing here decides what to render.

import { useCallback } from 'react';
import { getUsername } from '../utils/authSession';
import { notePageSlug } from '../utils/knowledgePages';
import { useEngagement } from './useEngagement';

export interface FilePageInput {
  title: string;
  content: string;
  // The conversation the text came from, so the filed page can point back at it.
  sessionId?: string;
  // Defaults to the name the consultant signed in with.
  addedBy?: string;
}

export type FilePageResult =
  | { ok: true; path: string; title: string }
  | { ok: false; reason: 'unavailable' };

export type RemovePageResult =
  | { ok: true }
  | { ok: false; reason: 'unavailable' };

export interface UseKnowledgePagesReturn {
  filePage: (input: FilePageInput) => Promise<FilePageResult>;
  removePage: (path: string) => Promise<RemovePageResult>;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export function useKnowledgePages(): UseKnowledgePagesReturn {
  const { currentEngagementId } = useEngagement();
  const base = currentEngagementId
    ? `/api/engagements/${encodeURIComponent(currentEngagementId)}/knowledge/pages`
    : null;

  const filePage = useCallback(async (input: FilePageInput): Promise<FilePageResult> => {
    const title = (input.title || '').trim();
    if (!base || !title) return { ok: false, reason: 'unavailable' };
    const addedBy = (input.addedBy || getUsername()).trim();
    const payload: Record<string, string> = { title, content: input.content || '' };
    if (input.sessionId) payload.sessionId = input.sessionId;
    if (addedBy) payload.addedBy = addedBy;
    try {
      const response = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return { ok: false, reason: 'unavailable' };
      const body = await readJson(response) as { path?: unknown; title?: unknown } | null;
      if (typeof body?.path !== 'string' || !body.path) return { ok: false, reason: 'unavailable' };
      return {
        ok: true,
        path: body.path,
        title: typeof body.title === 'string' && body.title ? body.title : title,
      };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  const removePage = useCallback(async (path: string): Promise<RemovePageResult> => {
    const slug = notePageSlug(path);
    if (!base || !slug) return { ok: false, reason: 'unavailable' };
    try {
      const response = await fetch(`${base}/${encodeURIComponent(slug)}`, { method: 'DELETE' });
      if (!response.ok) return { ok: false, reason: 'unavailable' };
      return { ok: true };
    } catch {
      return { ok: false, reason: 'unavailable' };
    }
  }, [base]);

  return { filePage, removePage };
}

export default useKnowledgePages;
